import React, { Suspense, lazy } from 'react';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { getPostLoginRedirectPath } from './access/routeAccess';
import { AppDocumentHead } from './components/AppDocumentHead';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import { InstallmentPostponePromptHost } from './features/installment-cards/postpone/InstallmentPostponePromptHost';
import { AuthProvider, useAuth } from './features/auth/AuthProvider';
import { PayzheBtpStatusBanner } from './features/bluetooth-printer/components/PayzheBtpStatusBanner';
import { PayzheBtpBootstrapEffect } from './features/bluetooth-printer/native-btp/PayzheBtpBootstrapEffect';
import i18n from './i18n';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { HR_EXTRAS_ENABLED } from './config/features';
import { PosModeRedirect } from './features/premium-pos/PosModeRedirect';
import { queryClient } from './lib/queryClient';
const DashboardPage = lazy(() => import('./features/dashboard/DashboardPage'));
const LoginPage = lazy(() => import('./features/auth/LoginPage'));
const PasswordResetPage = lazy(() => import('./features/auth/PasswordResetPage'));
const CustomersList = lazy(() => import('./features/customers/CustomersList'));
const CustomerProfile = lazy(() => import('./features/customers/CustomerProfile'));
const DIYRegistration = lazy(() => import('./features/customers/DIYRegistration'));
const ItemsList = lazy(() => import('./features/items/ItemsList'));
const ItemProfile = lazy(() => import('./features/items/ItemProfile'));
const BrandsList = lazy(() => import('./features/brands/BrandsList'));
const BrandProfile = lazy(() => import('./features/brands/BrandProfile'));
const SellersList = lazy(() => import('./features/sellers/SellersList'));
const SellerProfile = lazy(() => import('./features/sellers/SellerProfile'));
const AdminDashboard = lazy(() => import('./features/admin/AdminDashboard'));
const UserManagement = lazy(() => import('./features/admin/UserManagement'));
const SettingsPage = lazy(() => import('./features/settings/pages/SettingsPage'));

// Employees module components
const EmployeesModule = lazy(() => import('./features/employees/index'));
const AttendancePage = lazy(() => import('./features/employees/AttendancePage'));
const LeaveManagementPage = lazy(() => import('./features/employees/LeaveManagementPage'));
const SalaryRecordsPage = lazy(() => import('./features/employees/SalaryRecordsPage'));

// Zeettu module components
const ZeettuList = lazy(() => import('./features/zeettu/ZeettuList'));
const ZeettuProfile = lazy(() => import('./features/zeettu/ZeettuProfile'));

// Installment Card module components
const InstallmentCardsList = lazy(() => import('./features/installment-cards/InstallmentCardsList'));
const InstallmentCardProfile = lazy(() => import('./features/installment-cards/InstallmentCardProfile'));

// Store module components
const StoresList = lazy(() => import('./features/stores/StoresList'));
const StoreProfile = lazy(() => import('./features/stores/StoreProfile'));

// POS foundation (outlet, terminal, stock adjustment, POS sale)
const OutletsList = lazy(() => import('./features/outlets/OutletsList'));
const OutletCreate = lazy(() => import('./features/outlets/OutletCreate'));
const OutletProfile = lazy(() => import('./features/outlets/OutletProfile'));
const OutletEdit = lazy(() => import('./features/outlets/OutletEdit'));
const TerminalsList = lazy(() => import('./features/terminals/TerminalsList'));
const TerminalCreate = lazy(() => import('./features/terminals/TerminalCreate'));
const TerminalProfile = lazy(() => import('./features/terminals/TerminalProfile'));
const TerminalEdit = lazy(() => import('./features/terminals/TerminalEdit'));
const StockAdjustmentsList = lazy(() => import('./features/stock-adjustments/StockAdjustmentsList'));
const StockAdjustmentCreate = lazy(() => import('./features/stock-adjustments/StockAdjustmentCreate'));
const StockAdjustmentProfile = lazy(() => import('./features/stock-adjustments/StockAdjustmentProfile'));
const PosSalesList = lazy(() => import('./features/pos/PosSalesList'));
const PosSaleCheckout = lazy(() => import('./features/pos/PosSaleCheckout'));
const PosSaleProfile = lazy(() => import('./features/pos/PosSaleProfile'));
const PremiumPosRoutes = lazy(() => import('./features/premium-pos/PremiumPosRoutes'));
const PublicReceiptPage = lazy(() => import('./features/premium-pos/pos/PublicReceiptPage'));
const ThermalPrinterHelpPage = lazy(() => import('./features/bluetooth-printer/pages/ThermalPrinterHelpPage'));

// Route module components
const RoutesList = lazy(() => import('./features/routes/RoutesList'));
const RouteProfile = lazy(() => import('./features/routes/RouteProfile'));

// Vehicle module components
const VehiclesList = lazy(() => import('./features/vehicles/VehiclesList'));
const VehicleProfile = lazy(() => import('./features/vehicles/VehicleProfile'));

// Trip module components
const TripsList = lazy(() => import('./features/trip/TripsList'));
const TripProfile = lazy(() => import('./features/trip/TripProfile'));

// Samples module components
const SamplesList = lazy(() => import('./features/samples/SamplesList'));
const SampleProfile = lazy(() => import('./features/samples/SampleProfile'));
const SourceDocumentsList = lazy(() => import('./features/source-documents/SourceDocumentsList'));
const SourceDocumentProfile = lazy(() => import('./features/source-documents/SourceDocumentProfile'));

// Bundle module components
const BundlesList = lazy(() => import('./features/bundles/pages/BundlesList'));
const BundleProfile = lazy(() => import('./features/bundles/pages/BundleProfile'));

// Delivery Packages module components
const DeliveryPackagesList = lazy(() => import('./features/delivery-packages/DeliveryPackagesList'));
const DeliveryPackageProfile = lazy(() => import('./features/delivery-packages/DeliveryPackageProfile'));

// Payments module components
const PaymentsList = lazy(() => import('./features/payments/PaymentsList'));
const PaymentProfile = lazy(() => import('./features/payments/PaymentProfile'));

// Enquiries module components
const EnquiriesList = lazy(() => import('./features/installment-cards/enquiries/EnquiriesList'));
const RemunerationsList = lazy(() => import('./features/remuneration/RemunerationsList'));
const RemunerationProfile = lazy(() => import('./features/remuneration/RemunerationProfile'));
const ExpensesList = lazy(() => import('./features/expenses/ExpensesList'));
const ExpenseProfile = lazy(() => import('./features/expenses/ExpenseProfile'));
const GrnList = lazy(() => import('./features/grn/GrnList'));
const GrnProfile = lazy(() => import('./features/grn/GrnProfile'));
const ReturnToSupplierList = lazy(() => import('./features/return-to-supplier/ReturnToSupplierList'));
const ReturnToSupplierProfile = lazy(() => import('./features/return-to-supplier/ReturnToSupplierProfile'));
const ServiceTicketsList = lazy(() => import('./features/service-tickets/ServiceTicketsList'));
const ServiceTicketProfile = lazy(() => import('./features/service-tickets/ServiceTicketProfile'));
const ReplacementNotesList = lazy(() => import('./features/replacement-note/ReplacementNotesList'));
const ReplacementNoteProfile = lazy(() => import('./features/replacement-note/ReplacementNoteProfile'));
const ServiceReceiptsList = lazy(() => import('./features/service-receipt/ServiceReceiptsList'));
const ServiceReceiptProfile = lazy(() => import('./features/service-receipt/ServiceReceiptProfile'));

// Purchase Stock module components (unified page)
const PurchaseStockPage = lazy(() => import('./features/sellers/purchase-stock/PurchaseStockPage'));
const PurchaseReportsPage = lazy(() => import('./features/purchase-reports/PurchaseReportsPage'));
const PurchaseReportViewPage = lazy(() => import('./features/purchase-reports/PurchaseReportViewPage'));
const PurchaseStocksList = lazy(() => import('./features/sellers/purchase-stock/PurchaseStocksList'));
const PurchaseStockProfile = lazy(() => import('./features/sellers/purchase-stock/PurchaseStockProfile'));

// ZDR module components
const ZdrList = lazy(() => import('./features/zdr/pages/ZdrList'));
const ZdrDetails = lazy(() => import('./features/zdr/pages/ZdrDetails'));

// PDR module components
const PdrList = lazy(() => import('./features/pdr/pages/PdrList'));
const PdrCreate = lazy(() => import('./features/pdr/pages/PdrCreate'));
const PdrDetails = lazy(() => import('./features/pdr/pages/PdrDetails'));

// IRR module components
const IrrList = lazy(() => import('./features/irr/pages/IrrList'));
const IrrDetails = lazy(() => import('./features/irr/pages/IrrDetails'));
const RecoveryDashboardPage = lazy(
  () => import('./features/recovery-dashboard/pages/RecoveryDashboardPage'),
);

const StrList = lazy(() => import('./features/str/pages/StrList'));
const StrDetails = lazy(() => import('./features/str/pages/StrDetails'));

const AuditRunsList = lazy(() => import('./features/audit/pages/AuditRunsList'));
const AuditRunDetail = lazy(() => import('./features/audit/pages/AuditRunDetail'));
const DatabaseConfigPage = lazy(
  () => import('./features/database-config/pages/DatabaseConfigPage'),
);
const ZeettuCardAuditRunsList = lazy(
  () => import('./features/zeettu-card-audit/pages/ZeettuCardAuditRunsList'),
);
const ZeettuCardAuditRunDetail = lazy(
  () => import('./features/zeettu-card-audit/pages/ZeettuCardAuditRunDetail'),
);

// Store Stock module components
const StoreStockTripsList = lazy(() => import('./features/stock/store-stock/StoreStockTripsList'));
const StoreStockReportViewPage = lazy(() => import('./features/stock/store-stock/StoreStockReportViewPage'));
const StoreStockProfile = lazy(() => import('./features/stock/store-stock/StoreStockProfile'));
const StockItemProfile = lazy(() => import('./features/stock/stock-item/StockItemProfile'));
const StockItemSearchPage = lazy(() => import('./features/stock/stock-item/search/StockItemSearchPage'));

// Trip Stock module components
const TripStocksList = lazy(() => import('./features/trip-stock/TripStocksList'));
const TripStockPage = lazy(() => import('./features/trip-stock/TripStockPage'));
const TripStockProfile = lazy(() => import('./features/trip-stock/TripStockProfile'));
const TripStockByTripPage = lazy(() => import('./features/trip-stock/TripStockByTripPage'));

// Global loading spinner component
const GlobalSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading Zeettu...</p>
    </div>
  </div>
);

// Authentication guard component
const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAuthLoading } = useAuth();

  if (isAuthLoading) {
    return <GlobalSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/** Sends authenticated users to `/dashboard` (admin) or their first allowed module. */
const AuthenticatedEntryRedirect: React.FC = () => {
  const { getEffectiveRoles } = useAuth();
  return <Navigate to={getPostLoginRedirectPath(getEffectiveRoles())} replace />;
};

// Public route component (redirects authenticated users away from login/register)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAuthLoading } = useAuth();

  if (isAuthLoading) {
    return <GlobalSpinner />;
  }

  if (isAuthenticated) {
    return <AuthenticatedEntryRedirect />;
  }

  return <>{children}</>;
};

// 404 Not Found component
const NotFound = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-gray-400 mb-4">404</h1>
      <h2 className="text-2xl font-semibold text-gray-600 mb-4">Page Not Found</h2>
      <p className="text-gray-500 mb-8">The page you're looking for doesn't exist.</p>
      <a
        href="/"
        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
      >
        Go Home
      </a>
    </div>
  </div>
);

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        {/*
          React Router v7 wraps navigations in startTransition by default. A shared
          Suspense boundary that already resolved will keep the previous page visible
          while the URL updates (sidebar / blue badge clicks feel "stuck" until refresh).
          AppRoutes keys Suspense + ErrorBoundary by pathname so each navigation remounts.
        */}
        <BrowserRouter>
          <AuthProvider>
            <AppDocumentHead />
            <PayzheBtpBootstrapEffect />
            <PayzheBtpStatusBanner />
            <SessionTimeoutWarning />
            <InstallmentPostponePromptHost />
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </QueryClientProvider>
    </I18nextProvider>
  );
}

function AppRoutes() {
  const location = useLocation();
  const routeKey = location.pathname;

  return (
    <ErrorBoundary key={routeKey}>
      <Suspense key={routeKey} fallback={<GlobalSpinner />}>
        <Routes>
              {/* Public routes */}
              <Route path="/login" element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              } />
              <Route path="/forgot-password" element={
                <PublicRoute>
                  <PasswordResetPage />
                </PublicRoute>
              } />
              <Route path="/register" element={
                <PublicRoute>
                  <DIYRegistration />
                </PublicRoute>
              } />
              <Route path="/r/:token" element={<PublicReceiptPage />} />

              {/* Protected routes - wrapped in AuthGuard */}
              <Route path="/" element={
                <AuthGuard>
                  <AuthenticatedEntryRedirect />
                </AuthGuard>
              } />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DashboardPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Customer routes */}
              <Route
                path="/customers"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CustomersList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CustomerProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Item routes (admin only) */}
              <Route
                path="/items"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ItemsList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/items/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ItemProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Brand routes */}
              <Route
                path="/brands/:brandId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <BrandProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/brands"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <BrandsList />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Seller routes */}
              <Route
                path="/sellers"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SellersList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sellers/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SellerProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Store routes */}
              <Route
                path="/stores"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <StoresList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/stores/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <StoreProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* POS foundation: outlets & terminals (admin only); stock adjustments, sales */}
              <Route
                path="/pos/outlets/new"
                element={
                  <PosModeRedirect mode="legacy">
                    <ProtectedRoute>
                      <Layout>
                        <OutletCreate />
                      </Layout>
                    </ProtectedRoute>
                  </PosModeRedirect>
                }
              />
              <Route
                path="/pos/outlets/:id/edit"
                element={
                  <PosModeRedirect mode="legacy">
                    <ProtectedRoute>
                      <Layout>
                        <OutletEdit />
                      </Layout>
                    </ProtectedRoute>
                  </PosModeRedirect>
                }
              />
              <Route
                path="/pos/outlets/:id"
                element={
                  <PosModeRedirect mode="legacy">
                    <ProtectedRoute>
                      <Layout>
                        <OutletProfile />
                      </Layout>
                    </ProtectedRoute>
                  </PosModeRedirect>
                }
              />
              <Route
                path="/pos/outlets"
                element={
                  <PosModeRedirect mode="legacy">
                    <ProtectedRoute>
                      <Layout>
                        <OutletsList />
                      </Layout>
                    </ProtectedRoute>
                  </PosModeRedirect>
                }
              />
              <Route
                path="/pos/terminals/new"
                element={
                  <PosModeRedirect mode="legacy">
                    <ProtectedRoute>
                      <Layout>
                        <TerminalCreate />
                      </Layout>
                    </ProtectedRoute>
                  </PosModeRedirect>
                }
              />
              <Route
                path="/pos/terminals/:id/edit"
                element={
                  <PosModeRedirect mode="legacy">
                    <ProtectedRoute>
                      <Layout>
                        <TerminalEdit />
                      </Layout>
                    </ProtectedRoute>
                  </PosModeRedirect>
                }
              />
              <Route
                path="/pos/terminals/:id"
                element={
                  <PosModeRedirect mode="legacy">
                    <ProtectedRoute>
                      <Layout>
                        <TerminalProfile />
                      </Layout>
                    </ProtectedRoute>
                  </PosModeRedirect>
                }
              />
              <Route
                path="/pos/terminals"
                element={
                  <PosModeRedirect mode="legacy">
                    <ProtectedRoute>
                      <Layout>
                        <TerminalsList />
                      </Layout>
                    </ProtectedRoute>
                  </PosModeRedirect>
                }
              />
              <Route
                path="/pos/stock-adjustments/new"
                element={
                  <PosModeRedirect mode="legacy">
                    <ProtectedRoute>
                      <Layout>
                        <StockAdjustmentCreate />
                      </Layout>
                    </ProtectedRoute>
                  </PosModeRedirect>
                }
              />
              <Route
                path="/pos/stock-adjustments/:id"
                element={
                  <PosModeRedirect mode="legacy">
                    <ProtectedRoute>
                      <Layout>
                        <StockAdjustmentProfile />
                      </Layout>
                    </ProtectedRoute>
                  </PosModeRedirect>
                }
              />
              <Route
                path="/pos/stock-adjustments"
                element={
                  <PosModeRedirect mode="legacy">
                    <ProtectedRoute>
                      <Layout>
                        <StockAdjustmentsList />
                      </Layout>
                    </ProtectedRoute>
                  </PosModeRedirect>
                }
              />
              <Route
                path="/pos/sales/new"
                element={
                  <PosModeRedirect mode="legacy">
                    <ProtectedRoute>
                      <Layout>
                        <PosSaleCheckout />
                      </Layout>
                    </ProtectedRoute>
                  </PosModeRedirect>
                }
              />
              <Route
                path="/pos/sales/:id"
                element={
                  <PosModeRedirect mode="legacy">
                    <ProtectedRoute>
                      <Layout>
                        <PosSaleProfile />
                      </Layout>
                    </ProtectedRoute>
                  </PosModeRedirect>
                }
              />
              <Route
                path="/pos/sales"
                element={
                  <PosModeRedirect mode="legacy">
                    <ProtectedRoute>
                      <Layout>
                        <PosSalesList />
                      </Layout>
                    </ProtectedRoute>
                  </PosModeRedirect>
                }
              />

              {/* Premium POS: same screens under /premium-pos, shown when isPOSEnabled */}
              <Route path="/premium-pos/*" element={<PremiumPosRoutes />} />

              {/* Route routes */}
              <Route
                path="/routes"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RoutesList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/routes/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RouteProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Vehicle routes */}
              <Route
                path="/vehicles"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <VehiclesList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vehicles/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <VehicleProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Trip routes */}
              <Route
                path="/trip"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <TripsList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trip/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <TripProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Samples routes */}
              <Route
                path="/samples"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SamplesList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/samples/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SampleProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/source-documents"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SourceDocumentsList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/source-documents/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SourceDocumentProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Zeettu routes */}
              <Route
                path="/zeettu"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ZeettuList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/zeettu/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ZeettuProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/zeettu-card-audit"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ZeettuCardAuditRunsList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/zeettu-card-audit/:auditRunId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ZeettuCardAuditRunDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Installment Card routes */}
              <Route
                path="/installment-cards"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <InstallmentCardsList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/installment-cards/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <InstallmentCardProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Employees routes (admin only) */}
              <Route
                path="/employees/*"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <EmployeesModule />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employees/roles"
                element={<Navigate to="/settings?tab=access" replace />}
              />
              {HR_EXTRAS_ENABLED && (
                <>
                  <Route
                    path="/employees/attendance"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AttendancePage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/employees/leaves"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <LeaveManagementPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/employees/salary"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <SalaryRecordsPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                </>
              )}

              {/* Admin routes */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <AdminDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <UserManagement />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={<Navigate to="/settings" replace />}
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SettingsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Bundle routes */}
              <Route
                path="/bundles"
                element={
                  <ProtectedRoute requiredPermission="bundles.read">
                    <Layout>
                      <BundlesList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bundles/:id"
                element={
                  <ProtectedRoute requiredPermission="bundles.read">
                    <Layout>
                      <BundleProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Delivery Packages routes */}
              <Route
                path="/delivery-packages"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DeliveryPackagesList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/delivery-packages/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DeliveryPackageProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Payments routes */}
              <Route
                path="/payments"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PaymentsList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PaymentProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/thermal-printer"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ThermalPrinterHelpPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Enquiries routes */}
              <Route
                path="/enquiries"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <EnquiriesList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/grn"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <GrnList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/grn/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <GrnProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/return-to-supplier"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ReturnToSupplierList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/return-to-supplier/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ReturnToSupplierProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/service-tickets"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ServiceTicketsList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/service-tickets/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ServiceTicketProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/replacement-note"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ReplacementNotesList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/replacement-note/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ReplacementNoteProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/service-receipt"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ServiceReceiptsList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/service-receipt/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ServiceReceiptProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/remuneration"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RemunerationsList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/remuneration/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RemunerationProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/expenses"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ExpensesList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/expenses/:expenseVoucherId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ExpenseProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Purchase reports (list + view) */}
              <Route
                path="/purchase-reports"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PurchaseReportsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/purchase-reports/:id/view"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PurchaseReportViewPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* ZDR routes */}
              <Route
                path="/reports/zdr"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ZdrList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/zdr/create"
                element={<Navigate to="/reports/zdr" replace />}
              />
              <Route
                path="/reports/zdr/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ZdrDetails />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* PDR routes */}
              <Route
                path="/reports/pdr"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PdrList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/pdr/create"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PdrCreate />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/pdr/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PdrDetails />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* IRR routes */}
              <Route
                path="/reports/irr"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <IrrList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/irr/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <IrrDetails />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/reports/recovery-dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RecoveryDashboardPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/reports/str"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <StrList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/str/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <StrDetails />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/database-config"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DatabaseConfigPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/audit"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <AuditRunsList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/audit/:auditRunId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <AuditRunDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Purchase Stock routes - unified page (role-based) */}
              <Route
                path="/purchase-stock"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PurchaseStockPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              {/* Purchase Stocks List route (for managing purchase stock inventory) */}
              <Route
                path="/purchase-stocks-list"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PurchaseStocksList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/purchase-stock/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PurchaseStockProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Store Stock routes */}
              <Route
                path="/store-stock"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <StoreStockTripsList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/store-stock/report/purchase/:purchaseReportId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <StoreStockReportViewPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/store-stock/report/:purchaseTripId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <StoreStockReportViewPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/store-stock/purchase-report/:id/view"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <PurchaseReportViewPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/store-stock/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <StoreStockProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/stock-items"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <StockItemSearchPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/stock-items/:stockId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <StockItemProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Trip Stock routes */}
              <Route
                path="/trip-stock"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <TripStockPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trip-stock/list"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <TripStocksList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trip-stock/trip/:tripId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <TripStockByTripPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trip-stock/:id"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <TripStockProfile />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Delivery routes */}
              <Route
                path="/deliveries"
                element={
                  <ProtectedRoute requiredPermission="read:deliveries">
                    <Layout>
                      <div className="p-8 text-center">
                        <h1 className="text-2xl font-bold mb-4">Delivery Management</h1>
                        <p className="text-gray-600">Delivery management page coming soon...</p>
                      </div>
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* 404 route */}
              <Route path="*" element={
                <AuthGuard>
                  <NotFound />
                </AuthGuard>
              } />
            </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
